/**
 * PulseFeedbackView - Admin view of employee report validation feedback
 * Fetches from /api/admin/report-access/list (validation_data from employee report pages)
 * Shows visual analytics of accuracy data and allows closing feedback collection.
 */
import React, { useState, useEffect } from 'react';
import {
    MessageSquare, Users, Loader2, CheckCircle2,
    AlertTriangle, Clock, ChevronDown, ChevronRight,
    Lock, ShieldCheck, BarChart3, Trash2, Copy
} from 'lucide-react';
import { API_BASE_URL } from '../../services/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const swaPrincipal = localStorage.getItem('swaPrincipal');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
    };
};

// Map raw time_accuracy values to display labels and colors
const ACCURACY_CONFIG = {
    very_accurate:       { label: 'Very Accurate',       color: '#34C759', order: 0 },
    mostly_accurate:     { label: 'Mostly Accurate',     color: '#30D158', order: 1 },
    accurate_by_default: { label: 'No Feedback (Assumed Accurate)', color: '#007AFF', order: 2 },
    somewhat_inaccurate: { label: 'Somewhat Inaccurate', color: '#FF9500', order: 3 },
    very_inaccurate:     { label: 'Very Inaccurate',     color: '#FF3B30', order: 4 },
};

/** Pure-CSS horizontal bar for a single accuracy category */
function AccuracyBar({ label, count, total, color }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-xs, 12px)', marginBottom: 4, color: 'var(--color-text-secondary, #636366)' }}>
                <span>{label}</span>
                <span style={{ fontWeight: 600 }}>{count} ({pct}%)</span>
            </div>
            <div style={{ height: 22, borderRadius: 6, background: 'var(--color-bg-secondary, #F5F5F7)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 6, transition: 'width 0.4s ease' }} />
            </div>
        </div>
    );
}

function PulseFeedbackView({ department = null }) {
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [closing, setClosing] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [deduping, setDeduping] = useState(false);
    const [dedupResult, setDedupResult] = useState(null);

    const fetchInvites = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/api/admin/report-access/list`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load feedback data');
            const data = await res.json();
            setInvites(data.tokens || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchInvites(); }, []);

    const handleCloseFeedback = async () => {
        try {
            setClosing(true);
            const res = await fetch(`${API_BASE_URL}/api/admin/feedback/close`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to close feedback');
            setShowCloseConfirm(false);
            await fetchInvites(); // refresh data
        } catch (err) {
            setError(err.message);
        } finally {
            setClosing(false);
        }
    };

    const handleDeleteToken = async (tokenId) => {
        if (!window.confirm('Permanently delete this feedback entry? This cannot be undone.')) return;
        try {
            setDeleting(tokenId);
            const res = await fetch(`${API_BASE_URL}/api/admin/report-access/${tokenId}/permanent`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete feedback entry');
            await fetchInvites();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleting(null);
        }
    };

    const handleDedup = async () => {
        if (!window.confirm('Remove duplicate entries? This keeps the newest token per employee and deletes older duplicates.')) return;
        try {
            setDeduping(true);
            setDedupResult(null);
            const res = await fetch(`${API_BASE_URL}/api/admin/report-access/dedup`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to deduplicate');
            const data = await res.json();
            setDedupResult(data);
            await fetchInvites();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeduping(false);
        }
    };

    if (loading) {
        return (
            <div className="rp-loading">
                <Loader2 className="rp-spinner-icon" size={24} />
                <p>Loading employee feedback...</p>
            </div>
        );
    }

    if (error) {
        return <div className="rp-error">{error}</div>;
    }

    // Filter by department if provided
    let filtered = invites;
    if (department) {
        const deptLower = department.trim().toLowerCase();
        filtered = invites.filter(inv => (inv.department || '').trim().toLowerCase() === deptLower);
        if (filtered.length === 0) filtered = invites; // fallback to all
    }

    const totalSent = filtered.length;
    const explicitlyValidated = filtered.filter(inv => inv.validated_at && !(inv.validation_data || {}).auto_closed);
    const autoClosed = filtered.filter(inv => (inv.validation_data || {}).auto_closed);
    const pending = filtered.filter(inv => !inv.validated_at && !inv.is_revoked);
    const isClosed = pending.length === 0 && totalSent > 0;
    const responseRate = totalSent > 0 ? Math.round((explicitlyValidated.length / totalSent) * 100) : 0;

    // Build accuracy distribution — treat pending as "accurate_by_default"
    const accuracyCounts = {};
    Object.keys(ACCURACY_CONFIG).forEach(k => { accuracyCounts[k] = 0; });

    filtered.forEach(inv => {
        if (!inv.validated_at && !inv.is_revoked) {
            // Pending = assume accurate
            accuracyCounts['accurate_by_default'] = (accuracyCounts['accurate_by_default'] || 0) + 1;
        } else if (inv.validation_data) {
            const ta = inv.validation_data.time_accuracy || 'accurate_by_default';
            accuracyCounts[ta] = (accuracyCounts[ta] || 0) + 1;
        }
    });

    // Categorize into "Accurate" vs "Needs Attention"
    const accurateCount = (accuracyCounts['very_accurate'] || 0) + (accuracyCounts['mostly_accurate'] || 0) + (accuracyCounts['accurate_by_default'] || 0);
    const attentionCount = (accuracyCounts['somewhat_inaccurate'] || 0) + (accuracyCounts['very_inaccurate'] || 0);

    if (totalSent === 0) {
        return (
            <div className="rp-empty-state">
                <div className="rp-empty-icon"><MessageSquare size={32} /></div>
                <h3>No Report Links Sent Yet</h3>
                <p>Generate report links from the Manage tab, then come back here to see employee feedback.</p>
            </div>
        );
    }

    // Sort accuracy keys by display order
    const sortedKeys = Object.keys(accuracyCounts)
        .filter(k => ACCURACY_CONFIG[k])
        .sort((a, b) => ACCURACY_CONFIG[a].order - ACCURACY_CONFIG[b].order);

    return (
        <div className="pulse-feedback-view">
            {/* Closed banner */}
            {isClosed && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                    background: 'var(--color-bg-secondary, #F5F5F7)', borderRadius: 8,
                    marginBottom: '1.5rem', fontSize: 'var(--font-size-sm, 14px)',
                    border: '1px solid var(--color-border, #E5E5EA)'
                }}>
                    <Lock size={16} style={{ color: '#86868B' }} />
                    <span style={{ color: 'var(--color-text-secondary, #636366)' }}>
                        Feedback collection is closed. All non-responses have been recorded as accurate.
                    </span>
                </div>
            )}

            {/* Data Cleanup Tools */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem',
                flexWrap: 'wrap'
            }}>
                <button
                    onClick={handleDedup}
                    disabled={deduping}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 6,
                        background: 'var(--color-bg-secondary, #F5F5F7)',
                        border: '1px solid var(--color-border, #E5E5EA)',
                        cursor: deduping ? 'not-allowed' : 'pointer',
                        fontSize: 'var(--font-size-xs, 12px)',
                        color: 'var(--color-text-secondary, #636366)',
                        opacity: deduping ? 0.6 : 1,
                    }}
                >
                    {deduping ? <Loader2 size={14} className="rp-spinner-icon" /> : <Copy size={14} />}
                    {deduping ? 'Checking...' : 'Remove Duplicates'}
                </button>
                {dedupResult && (
                    <span style={{ fontSize: 'var(--font-size-xs, 12px)', color: dedupResult.duplicates_removed > 0 ? '#34C759' : 'var(--color-text-tertiary, #86868B)' }}>
                        {dedupResult.duplicates_removed > 0
                            ? `Removed ${dedupResult.duplicates_removed} duplicate${dedupResult.duplicates_removed !== 1 ? 's' : ''} from ${dedupResult.sessions_affected} employee${dedupResult.sessions_affected !== 1 ? 's' : ''}`
                            : 'No duplicates found'}
                    </span>
                )}
            </div>

            {/* Summary Stats */}
            <div className="ds-stat-row" style={{ marginBottom: '1.5rem' }}>
                <div className="ds-stat-card">
                    <div className="ds-stat-value">{totalSent}</div>
                    <div className="ds-stat-label">Reports Sent</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-value" style={{ color: '#34C759' }}>{explicitlyValidated.length}</div>
                    <div className="ds-stat-label">Feedback Received</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-value" style={{ color: pending.length > 0 ? '#FF9500' : '#34C759' }}>{pending.length}</div>
                    <div className="ds-stat-label">Awaiting Response</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-value">{responseRate}%</div>
                    <div className="ds-stat-label">Response Rate</div>
                </div>
            </div>

            {/* Accuracy Analytics */}
            <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                <div className="ds-section-header">
                    <h2><BarChart3 size={18} style={{ marginRight: 8, color: '#007AFF' }} /> Data Accuracy Overview</h2>
                </div>
                <div className="ds-section-body">
                    {/* Top-level summary: Accurate vs Needs Attention */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                        <div style={{
                            flex: 1, padding: 16, borderRadius: 10,
                            background: 'linear-gradient(135deg, rgba(52,199,89,0.08), rgba(52,199,89,0.03))',
                            border: '1px solid rgba(52,199,89,0.2)', textAlign: 'center'
                        }}>
                            <ShieldCheck size={24} style={{ color: '#34C759', marginBottom: 6 }} />
                            <div style={{ fontSize: 28, fontWeight: 700, color: '#34C759' }}>{accurateCount}</div>
                            <div style={{ fontSize: 'var(--font-size-xs, 12px)', color: 'var(--color-text-secondary, #636366)', marginTop: 2 }}>
                                Accurate ({totalSent > 0 ? Math.round((accurateCount / totalSent) * 100) : 0}%)
                            </div>
                        </div>
                        <div style={{
                            flex: 1, padding: 16, borderRadius: 10,
                            background: attentionCount > 0
                                ? 'linear-gradient(135deg, rgba(255,149,0,0.08), rgba(255,149,0,0.03))'
                                : 'var(--color-bg-secondary, #F5F5F7)',
                            border: attentionCount > 0
                                ? '1px solid rgba(255,149,0,0.2)'
                                : '1px solid var(--color-border, #E5E5EA)',
                            textAlign: 'center'
                        }}>
                            <AlertTriangle size={24} style={{ color: attentionCount > 0 ? '#FF9500' : '#86868B', marginBottom: 6 }} />
                            <div style={{ fontSize: 28, fontWeight: 700, color: attentionCount > 0 ? '#FF9500' : '#86868B' }}>{attentionCount}</div>
                            <div style={{ fontSize: 'var(--font-size-xs, 12px)', color: 'var(--color-text-secondary, #636366)', marginTop: 2 }}>
                                Needs Attention ({totalSent > 0 ? Math.round((attentionCount / totalSent) * 100) : 0}%)
                            </div>
                        </div>
                    </div>

                    {/* Detailed breakdown bars */}
                    <div style={{ fontSize: 'var(--font-size-xs, 12px)', fontWeight: 600, color: 'var(--color-text-tertiary, #86868B)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                        Breakdown
                    </div>
                    {sortedKeys.map(key => (
                        <AccuracyBar
                            key={key}
                            label={ACCURACY_CONFIG[key].label}
                            count={accuracyCounts[key]}
                            total={totalSent}
                            color={ACCURACY_CONFIG[key].color}
                        />
                    ))}
                </div>
            </div>

            {/* Explicit Feedback Details (excludes written feedback from chart, but shows in expandable rows) */}
            {explicitlyValidated.length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><CheckCircle2 size={18} style={{ marginRight: 8, color: '#34C759' }} /> Employee Feedback ({explicitlyValidated.length})</h2>
                    </div>
                    <div className="ds-section-body">
                        {explicitlyValidated.map(inv => {
                            const isExpanded = expandedId === inv.id;
                            const vd = inv.validation_data || {};
                            const taConfig = ACCURACY_CONFIG[vd.time_accuracy] || { label: vd.time_accuracy || '—', color: '#86868B' };
                            return (
                                <div key={inv.id} style={{ borderBottom: '1px solid var(--color-border, #E5E5EA)', padding: '12px 0' }}>
                                    <button
                                        onClick={() => setExpandedId(isExpanded ? null : inv.id)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                            textAlign: 'left', fontSize: 'var(--font-size-sm, 14px)'
                                        }}
                                    >
                                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                        <strong>{inv.employee_name}</strong>
                                        <span style={{ color: 'var(--color-text-tertiary, #86868B)', marginLeft: 4 }}>{inv.department}</span>
                                        <span style={{
                                            marginLeft: 'auto', fontSize: 'var(--font-size-xs, 12px)',
                                            padding: '2px 8px', borderRadius: 4,
                                            background: taConfig.color + '18', color: taConfig.color, fontWeight: 600
                                        }}>
                                            {taConfig.label}
                                        </span>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteToken(inv.id); }}
                                            disabled={deleting === inv.id}
                                            title="Delete this feedback entry"
                                            style={{
                                                display: 'flex', alignItems: 'center', padding: 4, marginLeft: 8,
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: 'var(--color-text-tertiary, #86868B)',
                                                opacity: deleting === inv.id ? 0.4 : 0.6,
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.color = '#FF3B30'; e.currentTarget.style.opacity = 1; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-tertiary, #86868B)'; e.currentTarget.style.opacity = 0.6; }}
                                        >
                                            {deleting === inv.id ? <Loader2 size={14} className="rp-spinner-icon" /> : <Trash2 size={14} />}
                                        </button>
                                    </button>
                                    {isExpanded && (
                                        <div style={{ marginTop: 12, marginLeft: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, background: 'var(--color-bg-secondary, #F5F5F7)', borderRadius: 8, padding: 16 }}>
                                            <div>
                                                <div style={{ fontSize: 'var(--font-size-xs, 12px)', color: 'var(--color-text-tertiary, #86868B)', marginBottom: 4, fontWeight: 600 }}>Time Accuracy</div>
                                                <div style={{ fontSize: 'var(--font-size-sm, 14px)' }}>{taConfig.label}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 'var(--font-size-xs, 12px)', color: 'var(--color-text-tertiary, #86868B)', marginBottom: 4, fontWeight: 600 }}>Over/Understated</div>
                                                <div style={{ fontSize: 'var(--font-size-sm, 14px)' }}>{vd.overstated_understated || '—'}</div>
                                            </div>
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <div style={{ fontSize: 'var(--font-size-xs, 12px)', color: 'var(--color-text-tertiary, #86868B)', marginBottom: 4, fontWeight: 600 }}>Missing Responsibilities</div>
                                                <div style={{ fontSize: 'var(--font-size-sm, 14px)' }}>{vd.missing_responsibilities || '—'}</div>
                                            </div>
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <div style={{ fontSize: 'var(--font-size-xs, 12px)', color: 'var(--color-text-tertiary, #86868B)', marginBottom: 4, fontWeight: 600 }}>Additional Feedback</div>
                                                <div style={{ fontSize: 'var(--font-size-sm, 14px)' }}>{vd.additional_feedback || '—'}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Pending / Awaiting Response */}
            {pending.length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><Clock size={18} style={{ marginRight: 8, color: '#FF9500' }} /> Awaiting Feedback ({pending.length})</h2>
                        <span style={{ fontSize: 'var(--font-size-xs, 12px)', color: 'var(--color-text-tertiary, #86868B)' }}>
                            Counted as accurate until feedback is received
                        </span>
                    </div>
                    <div className="ds-section-body">
                        {pending.map(inv => (
                            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--color-border, #E5E5EA)', fontSize: 'var(--font-size-sm, 14px)' }}>
                                <Users size={14} style={{ color: 'var(--color-text-tertiary, #86868B)' }} />
                                <span>{inv.employee_name}</span>
                                <span style={{ color: 'var(--color-text-tertiary, #86868B)' }}>{inv.department}</span>
                                <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xs, 12px)', color: 'var(--color-text-tertiary, #86868B)' }}>
                                    Sent {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                                    {inv.access_count > 0 && ` · Viewed ${inv.access_count}×`}
                                </span>
                                <button
                                    onClick={() => handleDeleteToken(inv.id)}
                                    disabled={deleting === inv.id}
                                    title="Delete this entry"
                                    style={{
                                        display: 'flex', alignItems: 'center', padding: 4, marginLeft: 8,
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: 'var(--color-text-tertiary, #86868B)',
                                        opacity: deleting === inv.id ? 0.4 : 0.6,
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#FF3B30'; e.currentTarget.style.opacity = 1; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-tertiary, #86868B)'; e.currentTarget.style.opacity = 0.6; }}
                                >
                                    {deleting === inv.id ? <Loader2 size={14} className="rp-spinner-icon" /> : <Trash2 size={14} />}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Close Feedback Button */}
            {pending.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                    {!showCloseConfirm ? (
                        <button
                            onClick={() => setShowCloseConfirm(true)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '10px 20px', borderRadius: 8,
                                background: 'var(--color-bg-secondary, #F5F5F7)',
                                border: '1px solid var(--color-border, #E5E5EA)',
                                cursor: 'pointer', fontSize: 'var(--font-size-sm, 14px)',
                                color: 'var(--color-text-secondary, #636366)',
                            }}
                        >
                            <Lock size={16} /> Close Feedback Collection
                        </button>
                    ) : (
                        <div style={{
                            padding: 16, borderRadius: 10,
                            background: 'rgba(255,59,48,0.05)', border: '1px solid rgba(255,59,48,0.2)',
                        }}>
                            <p style={{ margin: '0 0 12px', fontSize: 'var(--font-size-sm, 14px)' }}>
                                <strong>Are you sure?</strong> This will close feedback collection for all {pending.length} outstanding response{pending.length !== 1 ? 's' : ''}.
                                Non-responses will be permanently recorded as accurate. This cannot be undone.
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    onClick={handleCloseFeedback}
                                    disabled={closing}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        padding: '8px 16px', borderRadius: 6,
                                        background: '#FF3B30', color: '#fff', border: 'none',
                                        cursor: closing ? 'not-allowed' : 'pointer',
                                        opacity: closing ? 0.6 : 1,
                                        fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600,
                                    }}
                                >
                                    {closing ? <Loader2 size={14} className="rp-spinner-icon" /> : <Lock size={14} />}
                                    {closing ? 'Closing...' : 'Yes, Close Feedback'}
                                </button>
                                <button
                                    onClick={() => setShowCloseConfirm(false)}
                                    disabled={closing}
                                    style={{
                                        padding: '8px 16px', borderRadius: 6,
                                        background: 'transparent', border: '1px solid var(--color-border, #E5E5EA)',
                                        cursor: 'pointer', fontSize: 'var(--font-size-sm, 14px)',
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default PulseFeedbackView;
